import React, { forwardRef, useCallback, useRef, useState, ForwardedRef, CSSProperties } from 'react'
import { Empty } from 'antd'
import { AutoSizer, Grid, GridCellRenderer, OnScrollParams } from 'react-virtualized'

import CONFIG from '../../constants/config'
import Viewer from '../components/ViewerV2'
import Photo from '../components/Photo'
import backTop from '../../images/back_top.png'
import { capturePromiseException } from '../../utils/sentryUtils'
import { useAIPhoto, useAIPhotoDispatch } from './hooks/Context'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { getPhotoList } from './api'
import { ViewerRef } from '../components/ViewerV2/types'

interface RenderCellParams {
  autoSizerWidth: number
  columnIndex: number
  key: string
  rowIndex: number
  style: CSSProperties
}

const PhotosList = (_props: object, ref: ForwardedRef<Grid>) => {
  const {
    photos,
    checkedPhotos,
    unCheckedPhotos,
    checkAll,
    lastPageCond,
    filter
  } = useAIPhoto()
  const dispatch = useAIPhotoDispatch()
  const appDispatch = useAppDispatch()
  const groupID = useAppSelector((state) => state.group.groupId)
  const [autoSizerWidth, setAutoSizerWidth] = useState(0)
  const [visibleBackTop, setVisibleBackTop] = useState(false)
  const viewerRef = useRef<ViewerRef>(null)
  const scrollFetching = useRef(false)

  const handleFetchMore = useCallback(() => {
    if (lastPageCond && !scrollFetching.current) {
      scrollFetching.current = true
      appDispatch(
        getPhotoList({
          filter: {
            times: [
              {
                start: filter.times[0].format(CONFIG.timeFormat),
                end: filter.times[1].format(CONFIG.timeFormat)
              }
            ],
            labels: filter.labels
          },
          groupID,
          lastPageCond
        })
      )
        .then((results: any) => {
          dispatch({ type: 'setPhotos', data: [...photos, ...results.photos] })
          dispatch({ type: 'setLastPageCond', data: results.lastPageCond })
          scrollFetching.current = false
        })
        .catch(capturePromiseException)
    }
  }, [lastPageCond, scrollFetching.current, filter, groupID, photos])

  const handleGridScroll = useCallback(
    ({ clientHeight, scrollHeight, scrollTop }: OnScrollParams) => {
      if (scrollTop > window.screen.height - 250) {
        setVisibleBackTop(true)
      } else {
        setVisibleBackTop(false)
      }

      if (
        !scrollFetching.current &&
        scrollHeight > clientHeight && scrollHeight - clientHeight - scrollTop <= 0
      ) {
        console.log('fetch more')
        handleFetchMore()
      }
    },
    [scrollFetching.current, handleFetchMore]
  )

  const handleBackTopClick = () => {
    const dom = document.getElementsByClassName('virtualized-grid')[0] as HTMLElement
    if (dom) {
      dom.scrollTop = 0
    }
  }

  const renderCell = ({
    autoSizerWidth,
    columnIndex,
    key,
    rowIndex,
    style
  }: RenderCellParams) => {
    const columnCount = Math.floor(autoSizerWidth / 248)
    const index = columnCount * rowIndex + columnIndex
    const photo = photos[index]
    if (photo) {
      return (
        <div style={{ margin: '0 auto', ...style }} key={key}>
          <Photo
            style={{ margin: 0 }}
            photo={photo}
            canCheck={false}
            checked={false}
            readyToCheck={
              !!checkedPhotos.size || !!unCheckedPhotos.size || checkAll
            }
            onPreview={() => viewerRef.current?.view(index)}
          />
        </div>
      )
    } else {
      return null
    }
  }

  return (
    <>
      <div className="photoList" style={{}}>
        <AutoSizer onResize={({ width }) => setAutoSizerWidth(width)}>
          {({ width, height }) => {
            const columnCount = Math.floor(width / 248)
            const rowCount = Math.ceil(photos.length / columnCount)
            return (
              <Grid
                className="virtualized-grid"
                ref={ref}
                style={{ outline: 'none', scrollBehavior: 'smooth' }}
                width={width}
                height={height}
                columnWidth={248}
                rowHeight={310}
                columnCount={columnCount}
                rowCount={rowCount}
                cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                  renderCell({
                    autoSizerWidth: width,
                    columnIndex,
                    key: String(key),
                    rowIndex,
                    style
                  })
                }
                onScroll={handleGridScroll}
              />
            )
          }}
        </AutoSizer>
        <div
          className="isTop"
          style={{ opacity: visibleBackTop ? '1' : '0', right: '56px' }}
        >
          <img
            style={{ width: '48px' }}
            src={backTop}
            onClick={handleBackTopClick}
            alt=""
          />
        </div>
        {!photos.length && (
          <div className="empty-wrapper">
            <Empty description="无照片" />
          </div>
        )}
      </div>
      <Viewer ref={viewerRef} photos={photos} showWatermarkAction={false} />
    </>
  )
}

export default forwardRef(PhotosList)
